import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ApiError, api } from '@/lib/api';
import { useTenant } from '@/hooks/useTenant';
import type { CreateTenantOnboardingRequest } from '@muraka/shared';

type AssignmentDraft = {
  userId: string;
  role: 'manager' | 'user';
  isPrimary: boolean;
};

type FieldErrors = {
  name?: string;
  slug?: string;
  settings?: string;
  assignments?: string;
  starterTemplate?: string;
};

const STEPS = ['Tenant basics', 'Initial users/roles', 'Optional starter data', 'Review + submit'];

const BRAND_COLOR_PRESETS = [
  { label: 'Blue', value: '#3B82F6' },
  { label: 'Emerald', value: '#10B981' },
  { label: 'Violet', value: '#8B5CF6' },
  { label: 'Amber', value: '#F59E0B' },
  { label: 'Rose', value: '#F43F5E' },
  { label: 'Cyan', value: '#06B6D4' },
  { label: 'Orange', value: '#F97316' },
  { label: 'Slate', value: '#64748B' },
];

function createSlug(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function parseSettings(value: string): Record<string, unknown> {
  if (!value.trim()) {
    return {};
  }

  const parsed = JSON.parse(value) as unknown;
  if (!parsed || Array.isArray(parsed) || typeof parsed !== 'object') {
    throw new Error('Settings must be a JSON object');
  }

  return parsed as Record<string, unknown>;
}

export function AdminTenantOnboardingPage() {
  const navigate = useNavigate();
  const { isAdmin } = useTenant();

  const [stepIndex, setStepIndex] = useState(0);
  const [name, setName] = useState('');
  const [slugInput, setSlugInput] = useState('');
  const [settingsInput, setSettingsInput] = useState('{}');
  const [brandColor, setBrandColor] = useState('#3B82F6');
  const [assignments, setAssignments] = useState<AssignmentDraft[]>([
    { userId: '', role: 'manager', isPrimary: true },
  ]);
  const [starterEnabled, setStarterEnabled] = useState(false);
  const [starterTemplate, setStarterTemplate] = useState<'minimal' | 'sales'>('minimal');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const usersQuery = useQuery({
    queryKey: ['admin-users', 'onboarding'],
    queryFn: () => api.getUsers({ page: 1, pageSize: 100, sortBy: 'name', sortOrder: 'asc' }),
    enabled: isAdmin,
  });

  const normalizedSlug = useMemo(
    () => createSlug(slugInput.trim() ? slugInput : name),
    [name, slugInput]
  );

  const createMutation = useMutation({
    mutationFn: (payload: CreateTenantOnboardingRequest) => api.createTenantOnboarding(payload),
    onSuccess: result => {
      if (result.warnings.length > 0) {
        toast.warning('Tenant created with warnings. Review onboarding status for details.');
      } else {
        toast.success('Tenant onboarding completed.');
      }

      navigate({
        to: '/admin/tenants/onboarding/$tenantId/status',
        params: { tenantId: result.tenant.id },
      });
    },
    onError: error => {
      if (error instanceof ApiError && error.status === 409) {
        setFieldErrors(prev => ({ ...prev, slug: error.message }));
      }

      toast.error(error instanceof Error ? error.message : 'Failed to submit tenant onboarding');
    },
  });

  const validateStep = (index: number): boolean => {
    const nextErrors: FieldErrors = {};

    if (index === 0) {
      if (!name.trim()) {
        nextErrors.name = 'Tenant name is required';
      }

      if (!normalizedSlug) {
        nextErrors.slug = 'Slug is required';
      }

      try {
        parseSettings(settingsInput);
      } catch (error) {
        nextErrors.settings = error instanceof Error ? error.message : 'Invalid settings JSON';
      }
    }

    if (index === 1) {
      const selectedAssignments = assignments.filter(assignment => assignment.userId);
      if (selectedAssignments.length === 0) {
        nextErrors.assignments = 'At least one initial assignment is required';
      }

      const userIds = selectedAssignments.map(assignment => assignment.userId);
      const uniqueCount = new Set(userIds).size;
      if (uniqueCount !== userIds.length) {
        nextErrors.assignments = 'Duplicate users are not allowed in assignments';
      }

      const primaryCount = selectedAssignments.filter(assignment => assignment.isPrimary).length;
      if (primaryCount > 1) {
        nextErrors.assignments = 'Only one assignment can be marked primary';
      }
    }

    if (index === 2 && starterEnabled && !starterTemplate) {
      nextErrors.starterTemplate = 'Starter template is required when starter data is enabled';
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const goNext = () => {
    if (!validateStep(stepIndex)) {
      return;
    }

    setStepIndex(current => Math.min(current + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setStepIndex(current => Math.max(current - 1, 0));
  };

  const updateAssignment = (index: number, partial: Partial<AssignmentDraft>) => {
    setAssignments(current =>
      current.map((assignment, assignmentIndex) => {
        if (assignmentIndex !== index) {
          if (partial.isPrimary) {
            return { ...assignment, isPrimary: false };
          }

          return assignment;
        }

        return {
          ...assignment,
          ...partial,
        };
      })
    );
  };

  const addAssignment = () => {
    setAssignments(current => [...current, { userId: '', role: 'user', isPrimary: false }]);
  };

  const removeAssignment = (index: number) => {
    setAssignments(current => current.filter((_, assignmentIndex) => assignmentIndex !== index));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (!validateStep(0) || !validateStep(1) || !validateStep(2)) {
      setStepIndex(0);
      return;
    }

    const initialAssignments = assignments
      .filter(assignment => assignment.userId)
      .map(assignment => ({
        userId: assignment.userId,
        role: assignment.role,
        isPrimary: assignment.isPrimary,
      }));

    let settings: Record<string, unknown> = {};
    try {
      settings = parseSettings(settingsInput);
    } catch {
      setStepIndex(0);
      return;
    }

    if (/^#[0-9a-fA-F]{6}$/.test(brandColor)) {
      settings.brandColor = brandColor;
    }

    createMutation.mutate({
      tenant: {
        name: name.trim(),
        slug: normalizedSlug,
        settings,
      },
      initialAssignments,
      starterData: {
        enabled: starterEnabled,
        template: starterEnabled ? starterTemplate : undefined,
      },
    });
  };

  if (!isAdmin) {
    return (
      <AppLayout>
        <div className="space-y-4">
          <h1 className="text-2xl font-bold text-foreground">Onboarding</h1>
          <p className="text-muted-foreground">Admin access is required to view this page.</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Onboarding</h1>
          <p className="text-muted-foreground mt-1">
            Create a tenant, assign initial users, and optionally seed starter data.
          </p>
        </div>

        <div className="rounded-xl border border-border bg-card p-4">
          <div className="grid gap-2 md:grid-cols-4">
            {STEPS.map((step, index) => (
              <div
                key={step}
                className={`rounded-md border px-3 py-2 text-sm ${
                  index === stepIndex
                    ? 'border-primary text-primary font-medium'
                    : 'border-border text-muted-foreground'
                }`}
              >
                {index + 1}. {step}
              </div>
            ))}
          </div>
        </div>

        <form className="space-y-4" onSubmit={submit}>
          {stepIndex === 0 && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Tenant basics</h2>
              <Input
                label="Tenant name"
                value={name}
                onChange={event => setName(event.target.value)}
                required
                error={fieldErrors.name}
                placeholder="Acme Hospitality"
              />
              <Input
                label="Slug (optional)"
                value={slugInput}
                onChange={event => setSlugInput(event.target.value)}
                error={fieldErrors.slug}
                placeholder="acme-hospitality"
              />
              <p className="text-xs text-muted-foreground">
                Generated slug: {normalizedSlug || 'n/a'}
              </p>
              <Textarea
                label="Tenant settings (JSON)"
                value={settingsInput}
                onChange={event => setSettingsInput(event.target.value)}
                error={fieldErrors.settings}
                className="font-mono"
                rows={6}
              />
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Brand color</label>
                <p className="text-xs text-muted-foreground">
                  Choose a color to visually identify this tenant in the admin UI.
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {BRAND_COLOR_PRESETS.map(preset => (
                    <button
                      key={preset.value}
                      type="button"
                      title={preset.label}
                      onClick={() => setBrandColor(preset.value)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${
                        brandColor === preset.value
                          ? 'border-foreground scale-110'
                          : 'border-transparent hover:border-muted-foreground/40'
                      }`}
                      style={{ backgroundColor: preset.value }}
                    />
                  ))}
                  <Input
                    value={brandColor}
                    onChange={event => setBrandColor(event.target.value)}
                    placeholder="#3B82F6"
                    className="w-28 font-mono text-xs"
                  />
                  <span
                    className="inline-block w-6 h-6 rounded-full border border-border shrink-0"
                    style={{
                      backgroundColor: /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : '#ccc',
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {stepIndex === 1 && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-foreground">Initial users/roles</h2>
                <Button type="button" variant="outline" size="sm" onClick={addAssignment}>
                  Add assignment
                </Button>
              </div>

              {assignments.map((assignment, index) => (
                <div
                  key={`assignment-${index}`}
                  className="grid gap-3 rounded-md border border-border p-3 md:grid-cols-4"
                >
                  <Select
                    label="User"
                    value={assignment.userId}
                    onChange={event => updateAssignment(index, { userId: event.target.value })}
                    className="md:col-span-2"
                  >
                    <option value="">Select user</option>
                    {(usersQuery.data?.data ?? []).map(user => (
                      <option key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </option>
                    ))}
                  </Select>

                  <Select
                    label="Role"
                    value={assignment.role}
                    onChange={event =>
                      updateAssignment(index, { role: event.target.value as 'manager' | 'user' })
                    }
                  >
                    <option value="manager">Manager</option>
                    <option value="user">User</option>
                  </Select>

                  <div className="flex items-end justify-between gap-2">
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={assignment.isPrimary}
                        onChange={event =>
                          updateAssignment(index, { isPrimary: event.target.checked })
                        }
                        className="h-4 w-4 rounded border-border"
                      />
                      Primary
                    </label>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => removeAssignment(index)}
                      disabled={assignments.length === 1}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              ))}

              {fieldErrors.assignments && (
                <p className="text-sm text-destructive">{fieldErrors.assignments}</p>
              )}
            </div>
          )}

          {stepIndex === 2 && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Optional starter data</h2>
              <label className="flex items-center gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={starterEnabled}
                  onChange={event => setStarterEnabled(event.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                Enable starter data seeding
              </label>

              <Select
                label="Starter template"
                value={starterTemplate}
                onChange={event => setStarterTemplate(event.target.value as 'minimal' | 'sales')}
                disabled={!starterEnabled}
                error={fieldErrors.starterTemplate}
              >
                <option value="minimal">Minimal</option>
                <option value="sales">Sales</option>
              </Select>
            </div>
          )}

          {stepIndex === 3 && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <h2 className="text-lg font-semibold text-foreground">Review + submit</h2>
              <div className="text-sm space-y-2">
                <p>
                  <span className="text-muted-foreground">Tenant:</span> {name || 'n/a'}
                </p>
                <p>
                  <span className="text-muted-foreground">Slug:</span> {normalizedSlug || 'n/a'}
                </p>
                <p>
                  <span className="text-muted-foreground">Initial assignments:</span>{' '}
                  {assignments.filter(assignment => assignment.userId).length}
                </p>
                <p>
                  <span className="text-muted-foreground">Starter data:</span>{' '}
                  {starterEnabled ? `Enabled (${starterTemplate})` : 'Not requested'}
                </p>
                <p className="flex items-center gap-2">
                  <span className="text-muted-foreground">Brand color:</span>
                  <span
                    className="inline-block w-4 h-4 rounded-full border border-border"
                    style={{
                      backgroundColor: /^#[0-9a-fA-F]{6}$/.test(brandColor) ? brandColor : '#ccc',
                    }}
                  />
                  <span className="font-mono">{brandColor}</span>
                </p>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={goBack} disabled={stepIndex === 0}>
              Back
            </Button>

            <div className="flex items-center gap-2">
              {stepIndex < STEPS.length - 1 ? (
                <Button type="button" onClick={goNext}>
                  Next
                </Button>
              ) : (
                <Button type="submit" disabled={createMutation.isPending}>
                  {createMutation.isPending ? 'Submitting...' : 'Submit onboarding'}
                </Button>
              )}
            </div>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
